// Shared presentational toolkit for the Products admin forms.
// Goal: a consistent, modern, responsive look across CreateProductGenericForm,
// EditProductModal and ProductViewComponent — built only on RN primitives so it
// works on Expo Web (notebook/desktop/mobile) via `useWindowDimensions`.

import React from 'react';
import { View, Text, Pressable, StyleSheet, useWindowDimensions } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { TooltipCustom } from '../../../components/Tooltip';
import { ADMIN_COLORS, ADMIN_FONT } from '../../../config/theme/adminColors';

// FORM is kept as the local alias so existing imports across the Products forms keep
// working. The neutral tokens now live in the shared `adminColors` module so any admin
// screen can consume the same palette.
export const FORM = ADMIN_COLORS;

export const BREAKPOINTS = { mobile: 768, desktop: 1280 };

export function useResponsive() {
  const { width, height } = useWindowDimensions();
  const isMobile = width < BREAKPOINTS.mobile;
  const isDesktop = width >= BREAKPOINTS.desktop;
  return { width, height, isMobile, isDesktop, isTablet: !isMobile && !isDesktop };
}

// Comfortable modal content width across breakpoints.
export function useModalWidth() {
  const { width, isMobile } = useResponsive();
  return isMobile ? Math.round(width * 0.94) : Math.min(Math.round(width * 0.6), 760);
}

export const FieldLabel = ({ children, tooltip, tooltipHeight, required }) => (
  <View style={styles.labelRow}>
    <Text style={styles.labelText}>
      {children}
      {required ? <Text style={{ color: FORM.danger }}> *</Text> : null}
    </Text>
    {tooltip ? <TooltipCustom text={tooltip} height={tooltipHeight} /> : null}
  </View>
);

export const Hint = ({ children, style }) => (
  <Text style={[styles.hint, style]}>{children}</Text>
);

export const FieldError = ({ children }) =>
  children ? <Text style={styles.error}>{children}</Text> : null;

export const Field = ({ children, style }) => (
  <View style={[styles.field, style]}>{children}</View>
);

// Row that becomes a vertical stack on mobile.
export const Row = ({ children, gap = 16, style }) => {
  const { isMobile } = useResponsive();
  return (
    <View style={[{ flexDirection: isMobile ? 'column' : 'row', gap }, style]}>
      {children}
    </View>
  );
};

export const Col = ({ children, style }) => (
  <View style={[{ flex: 1, minWidth: 0 }, style]}>{children}</View>
);

export const SectionCard = ({ title, subtitle, right, children, style }) => (
  <View style={[styles.card, style]}>
    {title ? (
      <View style={styles.cardHeader}>
        <View style={{ flex: 1 }}>
          <Text style={styles.cardTitle}>{title}</Text>
          {subtitle ? <Text style={styles.cardSubtitle}>{subtitle}</Text> : null}
        </View>
        {right || null}
      </View>
    ) : null}
    {children}
  </View>
);

const BANNER_VARIANTS = {
  info: { bg: FORM.infoBg, border: FORM.infoBorder, text: FORM.infoText, icon: 'information-circle-outline' },
  warn: { bg: FORM.warnBg, border: FORM.warnBorder, text: FORM.warnText, icon: 'warning-outline' },
  success: { bg: FORM.successSoft, border: FORM.successBorder, text: FORM.success, icon: 'checkmark-circle-outline' },
  error: { bg: FORM.dangerSoft, border: FORM.dangerBorder, text: FORM.danger, icon: 'alert-circle-outline' },
};

export const InfoBanner = ({ children, variant = 'info', icon }) => {
  const c = BANNER_VARIANTS[variant] || BANNER_VARIANTS.info;
  return (
    <View style={[styles.banner, { backgroundColor: c.bg, borderColor: c.border }]}>
      <Ionicons name={icon || c.icon} size={18} color={c.text} style={{ marginTop: 1 }} />
      <Text style={[styles.bannerText, { color: c.text }]}>{children}</Text>
    </View>
  );
};

// Card-style radio group used for mutually-exclusive choices (e.g. validity mode).
export const SegmentedControl = ({
  options,
  value,
  onChange,
  accentColor = FORM.accent,
  disabled = false,
}) => {
  const { isMobile } = useResponsive();
  return (
    <View
      style={[
        styles.segmentWrap,
        { flexDirection: isMobile ? 'column' : 'row' },
        disabled && { opacity: 0.55 },
      ]}
      pointerEvents={disabled ? 'none' : 'auto'}
    >
      {options.map((opt) => {
        const selected = value === opt.value;
        return (
          <Pressable
            key={opt.value}
            onPress={() => onChange(opt.value)}
            style={({ hovered }) => [
              styles.segment,
              hovered && !selected && { borderColor: accentColor },
              selected && { borderColor: accentColor, backgroundColor: `${accentColor}14` },
            ]}
          >
            {opt.icon ? (
              <Ionicons
                name={opt.icon}
                size={22}
                color={selected ? accentColor : FORM.muted}
              />
            ) : null}
            <View style={{ flex: 1 }}>
              <Text style={[styles.segmentLabel, selected && { color: accentColor }]}>
                {opt.label}
              </Text>
              {opt.description ? <Text style={styles.segmentDesc}>{opt.description}</Text> : null}
            </View>
            <View style={[styles.radio, selected && { borderColor: accentColor }]}>
              {selected ? <View style={[styles.radioDot, { backgroundColor: accentColor }]} /> : null}
            </View>
          </Pressable>
        );
      })}
    </View>
  );
};

const BADGE_VARIANTS = {
  neutral: { bg: '#F3F4F6', text: '#374151' },
  success: { bg: FORM.successSoft, text: FORM.success },
  danger: { bg: FORM.dangerSoft, text: FORM.danger },
  info: { bg: FORM.infoBg, text: FORM.infoText },
  // Type tags com cor translúcida (fundo tingido + texto na cor forte)
  subscription: { bg: `${FORM.purple}1F`, text: FORM.purple }, // assinatura → roxo #7B1DA5
  product: { bg: `${FORM.accent}1F`, text: FORM.accent }, // produto → azul #2B93ED
};

export const Badge = ({ children, variant = 'neutral' }) => {
  const c = BADGE_VARIANTS[variant] || BADGE_VARIANTS.neutral;
  return (
    <View style={[styles.badge, { backgroundColor: c.bg }]}>
      <Text style={[styles.badgeText, { color: c.text }]}>{children}</Text>
    </View>
  );
};

// Read-only label/value row for the product summary card.
export const InfoRow = ({ label, children, last }) => (
  <View style={[styles.infoRow, last && { borderBottomWidth: 0 }]}>
    <Text style={styles.infoRowLabel}>{label}</Text>
    <View style={styles.infoRowValue}>
      {typeof children === 'string' || typeof children === 'number' ? (
        <Text style={styles.infoRowValueText}>{children}</Text>
      ) : (
        children
      )}
    </View>
  </View>
);

// Consistent style for native <Picker> (renders as <select> on web).
export const pickerStyle = {
  height: 48,
  width: '100%',
  marginTop: 6,
  borderRadius: 10,
  borderColor: FORM.border,
  borderWidth: 1,
  paddingHorizontal: 10,
  backgroundColor: FORM.surface,
  color: FORM.ink,
  fontFamily: ADMIN_FONT,
  fontSize: 15,
};

// Todo texto do toolkit declara ADMIN_FONT explicitamente. Sem isso ele caía no
// default do react-native ("System"), que casa por acaso no web mas não é o
// token do painel — e diverge de vez nos apps nativos.
const styles = StyleSheet.create({
  field: { marginBottom: 18 },
  labelRow: { flexDirection: 'row', alignItems: 'center', padding: 4 },
  labelText: { fontFamily: ADMIN_FONT, fontSize: 14, fontWeight: '600', color: FORM.label },
  hint: { fontFamily: ADMIN_FONT, fontSize: 12, color: FORM.muted, marginTop: 6, lineHeight: 16 },
  error: { fontFamily: ADMIN_FONT, color: FORM.danger, fontSize: 12, marginTop: 6, fontWeight: '500' },
  card: {
    backgroundColor: FORM.surface,
    borderRadius: FORM.radius,
    borderWidth: 1,
    borderColor: FORM.border,
    padding: 20,
    marginBottom: 16,
    boxShadow: '0 1px 2px rgba(16,24,40,0.04)',
  },
  cardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: FORM.border,
    paddingBottom: 12,
  },
  cardTitle: { fontFamily: ADMIN_FONT, fontSize: 16, fontWeight: '700', color: FORM.ink },
  cardSubtitle: { fontFamily: ADMIN_FONT, fontSize: 13, color: FORM.muted, marginTop: 4 },
  banner: {
    flexDirection: 'row',
    gap: 8,
    alignItems: 'flex-start',
    borderWidth: 1,
    borderRadius: 10,
    padding: 12,
    marginTop: 10,
  },
  bannerText: { flex: 1, fontFamily: ADMIN_FONT, fontSize: 13, lineHeight: 18 },
  segmentWrap: { gap: 10, marginTop: 8 },
  segment: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    borderWidth: 1.5,
    borderColor: FORM.border,
    borderRadius: 12,
    padding: 14,
    backgroundColor: FORM.surface,
    cursor: 'pointer',
  },
  segmentHover: { borderColor: FORM.accent },
  segmentSelected: { borderColor: FORM.accent, backgroundColor: FORM.accentSoft },
  segmentLabel: { fontFamily: ADMIN_FONT, fontSize: 14, fontWeight: '600', color: FORM.label },
  segmentDesc: { fontFamily: ADMIN_FONT, fontSize: 12, color: FORM.muted, marginTop: 2, lineHeight: 16 },
  radio: {
    width: 20,
    height: 20,
    borderRadius: 10,
    borderWidth: 2,
    borderColor: FORM.border,
    alignItems: 'center',
    justifyContent: 'center',
  },
  radioSelected: { borderColor: FORM.accent },
  radioDot: { width: 10, height: 10, borderRadius: 5, backgroundColor: FORM.accent },
  badge: { alignSelf: 'center', borderRadius: 999, paddingVertical: 3, paddingHorizontal: 10 },
  badgeText: { fontFamily: ADMIN_FONT, fontSize: 12, fontWeight: '600' },
  infoRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: FORM.border,
    gap: 12,
  },
  infoRowLabel: { fontFamily: ADMIN_FONT, fontSize: 13, color: FORM.muted },
  infoRowValue: { alignItems: 'flex-end', flexShrink: 1 },
  infoRowValueText: { fontFamily: ADMIN_FONT, fontSize: 14, fontWeight: '600', color: FORM.ink, textAlign: 'right' },
});
